import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PopupService } from '../../shared/modal/popup.service';
import { HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { AlertService } from '../../shared/alert/alert.service';
import { VariableDetails } from '../../shared/ontology/model/variable-details';
import { VariableTypeEnum } from '../../shared/ontology/variable-type.enum';
import { VariableService } from '../../shared/ontology/service/variable.service';
import { VariableValidationService } from '../../shared/ontology/service/variable-validation.service';
import { LotImportUpdateDescriptorsConfirmationDialogComponent } from './lot-import-update-descriptors-confirmation-dialog.component';
import { FileDownloadHelper } from '../sample/file-download.helper';
import { JhiEventManager } from '../../shared/core-utils';
import { parseFile } from '../../shared/util/file-utils';
import { ModalStockIdPrefixComponent } from '../../shared/modal/modal-stock-id-prefix.component';
import { LotTemplateExportType } from '../../shared/inventory/service/lot-template-export-type';
import { LotService } from '../../shared/inventory/service/lot.service';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

@Component({
    selector: 'jhi-lot-import-dialog',
    templateUrl: './lot-import-dialog.component.html'
})
export class LotImportDialogComponent implements OnInit, OnDestroy {

    @ViewChild('fileUpload')
        fileUpload: ElementRef;

    fileName = '';
    importData = new Array<any>();
    importHeader = [];
    attributesHeader = [];
    attributes: VariableDetails[] = [];
    isProcessing = false;
    staticHeaders = ['GID', 'STORAGE LOCATION ABBR', 'UNITS', 'AMOUNT', 'STOCK ID', 'NOTES'];
    subscriptionsManager = new SubscriptionsManager();

    importFormats = [
        { name: 'CSV', extension: '.csv' }, //
        { name: 'Excel', extension: '.xls,.xlsx' }
    ];

    selectedFileType = this.importFormats[1].extension; // '.xlsx'; // Set the default file type to CSV.

    constructor(
        private alertService: AlertService,
        public activeModal: NgbActiveModal,
        private modalService: NgbModal,
        private lotService: LotService,
        private fileDownloadHelper: FileDownloadHelper,
        private eventManager: JhiEventManager,
        private variableService: VariableService,
        private variableValidationService: VariableValidationService,
        private translateService: TranslateService) {

    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
    }

    close() {
        this.activeModal.dismiss('cancel');
    }

    async import() {

        this.isProcessing = true;
        const isValidAttributes = await this.processAttributes();

        if (this.validate()) {
            if (!isValidAttributes) {
                this.isProcessing = false;
                return;
            }
            try {
                await this.confirmAttributeStatus();
            } catch (e) {
                this.isProcessing = false;
                return;
            }

            const lotItems = this.importData
                .slice(1) // skip header
                .filter((row) => row.some((column) => Boolean(column.trim()))) // remove empty rows
                .map((row) => {
                    return Object.assign({
                        gid: row[this.importHeader[0]],
                        storageLocationAbbr: row[this.importHeader[1]],
                        unitName: row[this.importHeader[2]],
                        initialBalance: row[this.importHeader[3]],
                        stockId: row[this.importHeader[4]],
                        notes: row[this.importHeader[5]],
                        attributes: this.generateAttributesValuesMap(row)
                    });
                });

            const lotImportRequest = {
                'stockIdPrefix': '',
                'lotList': lotItems
            };

            if (this.validateStockIDInput(this.importData)) {
                const confirmModalRef = this.modalService.open(ModalStockIdPrefixComponent as Component);
                confirmModalRef.result.then((model) => {
                    lotImportRequest.stockIdPrefix = model.stockIdPrefix;
                    this.subscriptionsManager.add = this.lotService.importLotsWithInitialBalance(lotImportRequest).subscribe(
                        (res: HttpResponse<any>) => this.onSaveSuccess(res.body));
                }, () => {
                });
            } else {
                this.subscriptionsManager.add = this.lotService.importLotsWithInitialBalance(lotImportRequest).subscribe(
                    (res: HttpResponse<any>) => this.onSaveSuccess(res.body));
            }
        }
    }

    async processAttributes() {
        // Anything outside expected headers are considered codes.
        this.attributesHeader = this.importData[0].filter((column) =>
            !(<any>Object).values(this.staticHeaders).includes(column)
        ).map(this.toUpper);

        // Determine which of the codes are attributes
        this.attributes = await this.variableService.filterVariables({
            variableNames: this.attributesHeader,
            variableTypeIds: [VariableTypeEnum.INVENTORY_ATTRIBUTE.toString()],
            showObsoletes: true
        }).toPromise();

        const errorMessage: string[] = [];
        this.validateHeader(this.importData[0].map(this.toUpper), errorMessage, this.attributesHeader);

        if (errorMessage.length !== 0) {
            this.alertService.error('error.custom', { param: formatErrorList(errorMessage) });
            return false;
        }

        return true;
    }

    generateAttributesValuesMap(rowData: string[]) {
        const attributesValuesMap = {};
        this.attributes.forEach((attribute) => {
            if (this.attributesHeader.includes(this.toUpper(attribute.name))) {
                attributesValuesMap[this.toUpper(attribute.name)] = rowData[this.attributesHeader.indexOf(this.toUpper(attribute.name)) + 6];
            } else if (this.attributesHeader.includes(this.toUpper(attribute.alias))) {
                attributesValuesMap[this.toUpper(attribute.alias)] = rowData[this.attributesHeader.indexOf(this.toUpper(attribute.alias)) + 6];
            }
        });
        return attributesValuesMap;
    }

    toUpper(param: string) {
        return (param || '').toUpperCase();
    }

    clearSelectedFile(): void {
        this.importData = null;
    }

    onFileChange(evt: any) {
        const target = evt.target;
        this.fileName = target.files[0].name;

        const extension = this.fileName.split('.');
        if (extension[1].toLowerCase() !== 'xls' && extension[1].toLowerCase() !== 'xlsx') {
            this.fileName = '';
            target.value = '';
            this.alertService.error('inventoryManagerApp.error.import.lots.invalid.file.ext');
            return;
        }

        this.subscriptionsManager.add = parseFile(target.files[0], 'Lots').subscribe((value) => {
            this.importData = value;
        });
    }

    private async confirmAttributeStatus() {
        const header = this.importData[0].map(this.toUpper);
        // Convert rawData into map
        const data = this.importData.slice(1).map((fileRow) => {
            return fileRow.reduce((map, col, colIndex) => {
                map[header [colIndex]] = col;
                return map;
            }, {});
        });

        const attributeStatusById = this.variableValidationService.computeAttributeStatus(this.attributes, data);
        const invalidAttributeIds = Object.entries(attributeStatusById).filter((entry) => entry[1])
            .map((entry) => entry[0]);

        if (invalidAttributeIds.length) {
            const invalidAttributes = this.attributes.filter((attribute) => invalidAttributeIds.includes(attribute.id));
            const modalRef = this.modalService.open(LotImportUpdateDescriptorsConfirmationDialogComponent,
                { size: 'lg', backdrop: 'static' });
            modalRef.componentInstance.attributeStatusById = attributeStatusById;
            modalRef.componentInstance.attributes = invalidAttributes;
            return modalRef.result;
        }
    }

    private validateHeader(fileHeader: string[], errorMessage: string[], codes: string[]) {
        this.importHeader = [];
        this.staticHeaders.forEach((header) => {
            const result = fileHeader.filter((column) =>
                column.toLowerCase() === header.toLowerCase()
            );

            if (result.length === 0) {
                errorMessage.push(this.translateService.instant('error.import.header.mandatory', { param: this.staticHeaders.join(', ') }));
            }

            if (result.length > 1) {
                errorMessage.push(this.translateService.instant('error.import.header.duplicated', { param: header }));
            }

            const idx = fileHeader.indexOf(result[0]);
            this.importHeader.push(idx);
        });

        const duplicateColumns = codes.filter((header, i) => codes.indexOf(header) !== i);
        if (duplicateColumns.length >= 1) {
            errorMessage.push(this.translateService.instant('error.import.header.duplicated', { param: duplicateColumns.join(', ') }));
        }

        const invalidCodes = codes.filter((code) =>
            this.attributes.every((attribute) => this.toUpper(attribute.alias) !== this.toUpper(code) && this.toUpper(attribute.name) !== this.toUpper(code))
        );
        if (invalidCodes && invalidCodes.length > 0) {
            errorMessage.push(this.translateService.instant('inventoryManagerApp.error.import.invalid.codes', { param: invalidCodes.join(', ') }));
        }
    }

    private validateData(importData: any[]) {
        const errorMessages = [];
        for (let i = 0; i < importData.length; i++) {
            const row = importData[i];
            // GID
            if (row[this.importHeader[0]] && (isNaN(row[this.importHeader[0]]) || !Number.isInteger(Number(row[this.importHeader[0]]))
                || row[this.importHeader[0]] < 0)) {
                errorMessages.push(this.translateService.instant('inventoryManagerApp.lot.import.validation.gid'));
                break;
            }
            // Amount
            if (row[this.importHeader[3]] && (isNaN(row[this.importHeader[3]]) || row[this.importHeader[3]] < 0)) {
                errorMessages.push(this.translateService.instant('error.import.amount.format'));
                break;
            }
        }
        return errorMessages;
    }

    private validate() {
        if (!this.importData || this.importData.filter((row) => row.some((column) => Boolean(column.trim()))).length <= 1) {
            this.alertService.error('inventoryManagerApp.lot.import.validation.file.empty');
            return false;
        }

        const errorMessages: string[] = this.validateData(this.importData.slice(1));

        if (errorMessages.length !== 0) {
            this.alertService.error('error.custom', { param: formatErrorList(errorMessages) });
            return false;
        }
        return true;
    }

    private validateStockIDInput(importData: any[]) {
        return importData.slice(1).some((column) => !column[4]);
    }

    private onSaveSuccess(result: any) {
        this.alertService.success('inventoryManagerApp.lot.import.success');
        this.eventManager.broadcast({ name: 'columnFiltersChanged', content: 'OK' });
        this.activeModal.close(result);
    }

    export($event) {
        $event.preventDefault();
        this.subscriptionsManager.add = this.lotService.downloadLotsTemplate(LotTemplateExportType.LOT_CREATION).subscribe((response) => {

            const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(response);
            this.fileDownloadHelper.save(response.body, fileName);

        });
    }

}

@Component({
    selector: 'jhi-lot-import-popup',
    template: ''
})
export class LotImportPopupComponent implements OnInit, OnDestroy {

    routeSub: any;

    constructor(private route: ActivatedRoute,
                private popupService: PopupService
    ) {
    }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(() => {
            this.popupService
                .open(LotImportDialogComponent as Component);
        });
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }

}
